import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Search, Loader2, Phone, Mail, MapPin, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = { name: "", phone: "", email: "", notes: "", full_address: "", city: "", postal_code: "", label: "Utama" };

export default function CustomersPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    const params = q.trim() ? { q: q.trim() } : {};
    api.get("/customers", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const create = async () => {
    if (!form.name || !form.phone) {
      toast.error("Nama & No. HP wajib diisi");
      return;
    }
    try {
      const addresses = form.full_address
        ? [
            {
              label: form.label || "Utama",
              recipient: form.name,
              phone: form.phone,
              full_address: form.full_address,
              city: form.city,
              postal_code: form.postal_code,
              is_default: true,
            },
          ]
        : [];
      const { data } = await api.post("/customers", {
        name: form.name,
        phone: form.phone,
        email: form.email,
        notes: form.notes,
        addresses,
      });
      toast.success("Customer ditambahkan");
      setOpen(false);
      setForm(emptyForm);
      nav(`/customers/${data.id}`);
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal";
      toast.error(typeof d === "string" ? d : "Gagal");
    }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Data Customer
          </h1>
          <p className="text-sm text-slate-500 mt-1">Daftar pelanggan & alamat pengiriman.</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="add-customer-button">
          <UserPlus className="w-4 h-4" /> Tambah Customer
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="customers-search"
              placeholder="Cari nama / no HP / email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={load}>Cari</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <p className="text-slate-500">Belum ada customer.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/customers/${c.id}`}
              data-testid={`customer-card-${c.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-slate-900 text-white font-bold flex items-center justify-center text-lg shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                    <Phone className="w-3 h-3" /> {c.phone}
                  </div>
                  {c.email && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <Mail className="w-3 h-3" /> {c.email}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1 text-xs text-slate-500">
                <MapPin className="w-3 h-3" />
                {c.addresses?.length || 0} alamat tersimpan
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tambah Customer Baru</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
            <div>
              <Label>Nama Pelanggan *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="customer-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>No. HP *</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="customer-form-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Alamat Pertama (opsional)</div>
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div>
                  <Label>Label</Label>
                  <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Rumah / Kantor" />
                </div>
                <div>
                  <Label>Kota</Label>
                  <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
                <div>
                  <Label>Kode Pos</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Alamat Lengkap</Label>
                <Textarea rows={2} value={form.full_address} onChange={(e) => setForm((f) => ({ ...f, full_address: e.target.value }))} placeholder="Jalan, blok, nomor, RT/RW, kelurahan, kecamatan" data-testid="customer-form-address" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={create} className="bg-slate-900" data-testid="customer-form-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
